/**
 * Study-specific helpers
 */

import type { AxiosRequestConfig } from 'axios';
import _difference from 'lodash/difference';
import _get from 'lodash/get';
import _intersection from 'lodash/intersection';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';

import { _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';

import type { ID } from '@/model/Common.model';
import InVivoError from '@/model/InVivoError';
import type { Study } from '@/model/Study.model';

import { formatNumber } from '@/helpers';
import { ApiService } from '@/support/ApiService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { parseApiErrorResponse } from '@/utils/apiErrorResponse';

/** Raw value recorded against a measurement variable, as returned by the legacy API. */
export type MeasurementVariableValue = number | string | null | undefined;

/** A single measurement definition within a study type blueprint. */
export interface BlueprintMeasurement {
  name: string;
  variables: Array<string> | Record<string, string>;
}

/** Legacy study type blueprint describing the measurements and variables a study tracks. */
export interface StudyTypeBlueprint {
  activeValueName: string;
  displayName?: string;
  measurements: Record<string, BlueprintMeasurement>;
  variables: Record<string, unknown>;
}

/** Minimal study shape required by the blueprint helpers below. */
export interface StudyWithBlueprint {
  id: ID;
  measurement: string;
  typeBlueprint: StudyTypeBlueprint;
}

export interface ParsedStudyVariables {
  blueprint: StudyTypeBlueprint;
  measurementKey: string;
  topLevelVariables: Array<string>;
}

export const VARIABLES_NO_TOP_DISPLAY: Array<string> = ['notes'];

const _PARSED_VARIABLES: Record<ID, ParsedStudyVariables> = {};

/**
 * Given a study, prepare the top-level variables from the blueprint, being
 * sensitive to whether the study has measurements or not.
 */
export const parseVariables = (study: StudyWithBlueprint): ParsedStudyVariables => {
  if (_isNil(_PARSED_VARIABLES[study.id])) {
    const blueprint = study.typeBlueprint;
    let measurementKey = '';
    let topLevelVariables: Array<string> = [];

    if (_isNotBlank(study.measurement)) {
      measurementKey = blueprint.measurements[study.measurement].name;
      topLevelVariables = [measurementKey, ...Object.keys(blueprint.variables)];
    } else {
      measurementKey = blueprint.activeValueName;
      topLevelVariables = [...Object.keys(blueprint.variables)];
    }

    topLevelVariables = _difference(topLevelVariables, VARIABLES_NO_TOP_DISPLAY);

    _PARSED_VARIABLES[study.id] = {
      blueprint,
      measurementKey,
      topLevelVariables,
    };
  }
  return _PARSED_VARIABLES[study.id];
};

/**
 * Calculate a measurement in real-time
 */
export const calculate = (
  studyType: string,
  measurement: string,
  variables: Record<string, MeasurementVariableValue> = {}
): number | undefined => {
  let calc: MeasurementVariableValue;

  // Xenograft: Three Variables
  if ((studyType === 'xenograft' || studyType === 'bioluminescenceSubQ') && measurement === 'threeVariables') {
    calc =
      Number(variables['threeVariables:length']) *
      Number(variables['threeVariables:width']) *
      Number(variables['threeVariables:depth']) *
      0.5236;

    // Xenograft: Two Variables
  } else if ((studyType === 'xenograft' || studyType === 'bioluminescenceSubQ') && measurement === 'twoVariables') {
    calc =
      (Number(variables['twoVariables:length']) *
        Number(variables['twoVariables:width']) *
        Number(variables['twoVariables:width'])) /
      2;
  }

  // Xenograft: Two Variables TRIPLICATE
  else if (studyType === 'xenograft' && measurement === 'twoTriplicate') {
    const firstMeasurement =
      (Math.pow(Number(variables['twoTriplicate:width1']), 2) * Number(variables['twoTriplicate:length1'])) / 2;
    const secondMeasurement =
      (Math.pow(Number(variables['twoTriplicate:width2']), 2) * Number(variables['twoTriplicate:length2'])) / 2;
    const thirdMeasurement =
      (Math.pow(Number(variables['twoTriplicate:width3']), 2) * Number(variables['twoTriplicate:length3'])) / 2;
    calc = (firstMeasurement + secondMeasurement + thirdMeasurement) / 3;
  }

  // Xenograft: Three Variables TRIPLICATE
  else if (studyType === 'xenograft' && measurement === 'threeTriplicate') {
    const firstMeasurement =
      Number(variables['threeTriplicate:width1']) *
      Number(variables['threeTriplicate:length1']) *
      Number(variables['threeTriplicate:depth1']) *
      0.5236;

    const secondMeasurement =
      Number(variables['threeTriplicate:width2']) *
      Number(variables['threeTriplicate:length2']) *
      Number(variables['threeTriplicate:depth2']) *
      0.5236;

    const thirdMeasurement =
      Number(variables['threeTriplicate:width3']) *
      Number(variables['threeTriplicate:length3']) *
      Number(variables['threeTriplicate:depth3']) *
      0.5236;

    calc = (firstMeasurement + secondMeasurement + thirdMeasurement) / 3;
  }

  // Volume only tumour studies
  else if (studyType === 'xenograft' && measurement === 'volume') {
    calc = variables['volume:volume'];
  }

  // Leukaemia: Disease Burden %
  else if (studyType === 'leukaemia' && measurement === 'diseaseBurdenPc') {
    calc =
      (Number(variables['diseaseBurdenPc:hcd45']) /
        (Number(variables['diseaseBurdenPc:hcd45']) + Number(variables['diseaseBurdenPc:mcd45']))) *
      100;
  } else if (studyType === 'leukaemia' && measurement === 'diseaseBurdenPcOnly') {
    calc = variables['diseaseBurdenPcOnly:percent'];
  } else if (studyType === 'rettModel') {
    calc =
      Number(variables['gpcs:mobility']) +
      Number(variables['gpcs:gait']) +
      Number(variables['gpcs:hindlimbClasping']) +
      Number(variables['gpcs:tremor']) +
      Number(variables['gpcs:generalCondition']);
  }

  // No match? Return nothing.
  else {
    return;
  }

  if (typeof calc === 'number' && Number.isNaN(calc)) {
    calc = 0;
  }

  const rounded = (Math.round(Number(calc) * 100) / 100).toFixed(2);

  return Number(rounded);
};

export const shouldAverageMetric = (metricName: string): boolean => metricName !== 'notes';

/**
 * Resolve the display value for a single measurement cell: the formatted data point when one
 * exists, otherwise a blank or zero placeholder depending on whether the measurement records
 * any of the blueprint variables for the study's active measurement.
 */
export const measurementController = (
  study: StudyWithBlueprint,
  measurement: Record<string, MeasurementVariableValue>,
  calculationToView: string
): number | string => {
  const dataPoint = _get(measurement, calculationToView);
  const { blueprint } = parseVariables(study);

  if (_notNil(dataPoint) && Boolean(dataPoint)) {
    return formatNumber(dataPoint);
  } else if (calculationToView === 'weight') {
    return '';
  } else {
    const varReturn = _intersection(
      Object.keys(measurement),
      _map(blueprint.measurements[study.measurement].variables, (variable) => `${study.measurement}:${variable}`)
    );

    return _isEmpty(varReturn) ? '' : 0;
  }
};

export const isClosed = (study?: Pick<Study, 'archived_at' | 'canceled_at'> | null): boolean =>
  Boolean(study?.archived_at) || Boolean(study?.canceled_at);

const FAILED_TO_RESUME_STUDY = 'Failed to resume study';

/**
 * Toast copy for a failed resume. Java 400s put the specific reason on `errors` (reason for
 * change, "Study is not completed.", "Study is not cancelled.") and a generic summary on
 * `message`. Prefer the field messages, then the summary, then the generic fallback.
 */
export const resumeStudyErrorMessage = (error: unknown): string => {
  const { summary, details } = parseApiErrorResponse(error);
  const fieldMessages = details.reduce<Array<string>>((messages, detail) => {
    messages.push(...detail.messages);
    return messages;
  }, []);

  if (_isNotEmpty(fieldMessages)) {
    return fieldMessages.join(' ');
  }
  if (_isNotBlank(summary)) {
    return summary;
  }
  return FAILED_TO_RESUME_STUDY;
};

/**
 * Resumes a completed (unarchive) or cancelled (uncancel) study through the Java API, sending the
 * reason for change when one was given. Failures are reported to the exception handler and rethrown
 * so callers can show their own error feedback.
 */
export const resumeStudy = async (
  study: Pick<Study, 'api_id' | 'archived_at'>,
  reasonForChange?: string
): Promise<void> => {
  const isCompleted = _notNil(study.archived_at);
  const slug = isCompleted ? 'study-unarchive' : 'study-uncancel';
  const body = _isNotBlank(reasonForChange) ? { reason_for_change: reasonForChange } : undefined;
  const options = {
    requestConfigOverrides: { redirect: { 403: false } } as AxiosRequestConfig,
    onError: { throw: true, toast: false, capture: false, slug },
  };
  try {
    if (isCompleted) {
      await ApiService.call({
        endpoint: 'POST /api/v1/studies/{studyApiId}/unarchive',
        path: { studyApiId: study.api_id },
        body,
        options,
      });
    } else {
      await ApiService.call({
        endpoint: 'POST /api/v1/studies/{studyApiId}/uncancel',
        path: { studyApiId: study.api_id },
        body,
        options,
      });
    }
  } catch (error) {
    ExceptionHandler.captureException(
      new InVivoError(isCompleted ? 'Failed to unarchive study' : 'Failed to uncancel study', { cause: error, slug })
    );
    throw error;
  }
};
